import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Loader2, CalendarRange, ShoppingBag, IndianRupee, PackageSearch, Eye, X, Store, Phone, Pencil, Save, Plus, Minus, Trash2, ArrowLeft, XCircle, History, Check, Package } from "lucide-react";
import { api, formatMoney, priceOptions, effectivePrice, formatApiErrorDetail, productImageUrl } from "@/lib/api";
import OrderStatusBadge from "@/components/OrderStatusBadge";
import ProductImage from "@/components/ProductImage";

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom Date" },
  { key: "all", label: "All Time" },
];

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const dayEnd = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const fmt = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

function rangeFor(preset, from, to) {
  const now = new Date();
  if (preset === "today") return [dayStart(now), dayEnd(now)];
  if (preset === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); return [dayStart(y), dayEnd(y)]; }
  if (preset === "week") { const s = new Date(now); const day = (s.getDay() + 6) % 7; s.setDate(s.getDate() - day); return [dayStart(s), dayEnd(now)]; }
  if (preset === "month") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return [dayStart(s), dayEnd(now)]; }
  if (preset === "custom") return [from ? dayStart(new Date(from)) : null, to ? dayEnd(new Date(to)) : null];
  return [null, null];
}

const cleanFreeName = (n) => (n || "").replace(/\s*\(FREE\)\s*$/i, "").trim();

function OrderDetailsModal({ order: initialOrder, onClose, onUpdated }) {
  const [order, setOrder] = useState(initialOrder);
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [products, setProducts] = useState(null);
  const [pickerStep, setPickerStep] = useState(null); // null | "brands" | "products"
  const [pickerBrand, setPickerBrand] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const isPending = order.status === "Pending";

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const productsById = useMemo(() => {
    const m = {};
    (products || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);

  const startEdit = async () => {
    setError("");
    if (!products) {
      try {
        const { data } = await api.get("/products");
        setProducts(data);
      } catch (err) {
        setError("Could not load products for editing.");
        return;
      }
    }
    const items = (order.items || []).filter((it) => !it.is_free).map((it) => ({
      product_id: it.product_id, name: it.name, brand: it.brand,
      unit: it.unit || null, qty: it.qty,
    }));
    setEditItems(items);
    setEditing(true);
  };

  const runPreview = useCallback(async (items) => {
    const payload = { items: items.filter((r) => Number(r.qty) >= 1).map((r) => ({ product_id: r.product_id, unit: r.unit || null, qty: Number(r.qty) })) };
    if (payload.items.length === 0) { setPreview({ items: [], total: 0 }); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/orders/compute", payload);
      setPreview(data);
    } catch (err) {
      /* keep last preview on transient error */
    } finally { setPreviewing(false); }
  }, []);

  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => runPreview(editItems), 250);
    return () => clearTimeout(t);
  }, [editItems, editing, runPreview]);

  const lineFor = (row) => {
    const items = (preview && preview.items) || [];
    return items.find((it) => !it.is_free && it.product_id === row.product_id && (it.unit || null) === (row.unit || null)) || null;
  };
  const freeLines = ((preview && preview.items) || []).filter((it) => it.is_free);

  const setQty = (idx, val) => setEditItems((rows) => rows.map((r, i) => i === idx ? { ...r, qty: val } : r));
  const incQty = (idx) => setEditItems((rows) => rows.map((r, i) => i === idx ? { ...r, qty: (Number(r.qty) || 0) + 1 } : r));
  const decQty = (idx) => setEditItems((rows) => rows.map((r, i) => i === idx ? { ...r, qty: Math.max(1, (Number(r.qty) || 1) - 1) } : r));
  const setUnit = (idx, unit) => setEditItems((rows) => rows.map((r, i) => i === idx ? { ...r, unit: unit || null } : r));
  const removeRow = (idx) => setEditItems((rows) => rows.filter((_, i) => i !== idx));

  const addProduct = (p) => {
    const opts = priceOptions(p);
    const unit = opts[0]?.unit || null;
    setEditItems((rows) => {
      const existing = rows.findIndex((r) => r.product_id === p.id && (r.unit || null) === (unit || null));
      if (existing >= 0) return rows.map((r, i) => i === existing ? { ...r, qty: (Number(r.qty) || 0) + 1 } : r);
      return [...rows, { product_id: p.id, name: p.name, brand: p.brand, unit, qty: 1 }];
    });
    setPickerStep(null);
    setPickerBrand(null);
  };

  const sellableProducts = useMemo(
    () => (products || []).filter((p) => priceOptions(p).length > 0 || effectivePrice(p) != null),
    [products]
  );
  const pickerBrands = useMemo(() => {
    const s = new Set();
    sellableProducts.forEach((p) => { if (p.brand) s.add(p.brand); });
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [sellableProducts]);
  const brandProducts = useMemo(
    () => sellableProducts.filter((p) => p.brand === pickerBrand).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [sellableProducts, pickerBrand]
  );

  const doSave = async () => {
    setSaving(true); setError("");
    try {
      const payload = { items: editItems.filter((r) => Number(r.qty) >= 1).map((r) => ({ product_id: r.product_id, unit: r.unit || null, qty: Number(r.qty) })), note: order.note || "" };
      const { data } = await api.put(`/staff/orders/${order.id}`, payload);
      setOrder(data);
      setEditing(false); setConfirmSave(false);
      onUpdated && onUpdated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save changes.");
      setConfirmSave(false);
    } finally { setSaving(false); }
  };

  const doCancelOrder = async () => {
    if (!cancelReason.trim()) { setError("Please enter a cancellation reason."); return; }
    setSaving(true); setError("");
    try {
      const { data } = await api.post(`/staff/orders/${order.id}/cancel`, { reason: cancelReason.trim() });
      setOrder(data);
      setCancelMode(false); setCancelReason("");
      onUpdated && onUpdated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel order.");
    } finally { setSaving(false); }
  };

  const paidItems = (order.items || []).filter((it) => !it.is_free);
  const viewFree = (order.items || []).filter((it) => it.is_free);
  const freeByProduct = viewFree.reduce((acc, f) => { (acc[f.product_id || "_"] = acc[f.product_id || "_"] || []).push(f); return acc; }, {});
  const grandTotal = editing ? (preview ? preview.total : order.total) : order.total;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div data-testid="staff-order-details-modal" className="relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 p-4 sm:p-5">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-800">Order #{order.id.slice(-6).toUpperCase()}</h3>
            <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString("en-IN")}</p>
          </div>
          <button data-testid="staff-order-details-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-5">
          <div className="mb-3 rounded-lg bg-slate-50 p-3">
            <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
              <Store className="h-4 w-4 text-blue-500" /> {order.shop_name || order.customer_name || "—"}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
              {order.customer_name && <span>{order.customer_name}</span>}
              <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-blue-400" />{order.customer_phone || "—"}</span>
            </div>
            <div className="mt-2 flex items-center gap-2"><OrderStatusBadge status={order.status} /></div>
            {order.cancel_reason && <p className="mt-1 text-xs font-medium text-red-600">Cancellation reason: {order.cancel_reason}</p>}
          </div>

          {error && <p data-testid="staff-order-error" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{error}</p>}

          {!editing ? (
            <>
              <div className="space-y-2" data-testid="staff-order-items">
                {paidItems.map((it, idx) => (
                  <div key={idx} data-testid={`staff-order-item-${idx}`} className="rounded-lg border border-slate-100 p-2.5">
                    <p className="text-sm font-semibold text-slate-800">{it.name}</p>
                    <div className="mt-1.5 grid grid-cols-4 gap-2 text-xs">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">Unit</p>
                        <p className="truncate font-medium text-slate-700">{it.unit || "—"}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">Qty</p>
                        <p className="font-medium text-slate-700">{it.qty}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">Price</p>
                        <p className="truncate font-medium text-slate-700">{formatMoney(it.price) || "—"}</p>
                      </div>
                      <div className="min-w-0 text-right">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">Amount</p>
                        <p className="truncate font-bold text-blue-700">{formatMoney(it.line_total) || "—"}</p>
                      </div>
                    </div>
                    {(freeByProduct[it.product_id] || []).map((f, fi) => (
                      <p key={`f-${idx}-${fi}`} className="mt-1.5 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        🎁 {f.qty} {f.unit ? `${f.unit} ` : ""}{cleanFreeName(f.name)} — FREE (₹0)
                      </p>
                    ))}
                    {it.offer_discount ? (
                      <p className="mt-1.5 flex justify-between rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        <span>Cash Discount</span><span>-{formatMoney(it.offer_discount)}</span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-sm font-bold text-slate-800">
                <span>Order Total</span>
                <span data-testid="staff-order-details-total" className="text-blue-700">{formatMoney(order.total) || "—"}</span>
              </div>
              {order.note ? <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Note: {order.note}</p> : null}

              {(order.edit_history || []).length > 0 && (
                <div className="mt-3">
                  <button data-testid="staff-order-history-toggle" onClick={() => setShowHistory((s) => !s)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
                    <History className="h-3.5 w-3.5" /> {showHistory ? "Hide" : "Show"} edit history ({order.edit_history.length})
                  </button>
                  {showHistory && (
                    <div data-testid="staff-order-history" className="mt-2 space-y-2">
                      {order.edit_history.map((h, i) => (
                        <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs text-slate-600">
                          <p className="font-semibold text-slate-700">{h.action === "cancel" ? "Cancelled" : h.action === "status" ? `Status → ${h.new_status}` : "Edited"} · {h.by} <span className="font-normal text-slate-400">({h.by_role})</span></p>
                          <p className="text-[11px] text-slate-400">{new Date(h.at).toLocaleString("en-IN")}</p>
                          {h.reason && <p className="mt-0.5 text-red-600">Reason: {h.reason}</p>}
                          {(h.changes || []).map((c, ci) => <p key={ci} className="mt-0.5">• {c}</p>)}
                          {(h.old_total != null && h.new_total != null) && <p className="mt-0.5 text-slate-500">Total: {formatMoney(h.old_total)} → <b>{formatMoney(h.new_total)}</b></p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isPending && !cancelMode && (
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <button data-testid="staff-edit-order-button" onClick={startEdit} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
                    <Pencil className="h-4 w-4" /> Edit Order
                  </button>
                  <button data-testid="staff-cancel-order-button" onClick={() => { setCancelMode(true); setError(""); }} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
                    <XCircle className="h-4 w-4" /> Cancel Order
                  </button>
                  <button data-testid="staff-ok-button" onClick={onClose} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                    <Check className="h-4 w-4" /> OK
                  </button>
                </div>
              )}
              {!isPending && (
                <div className="mt-4">
                  {order.status !== "Cancelled" && (
                    <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">This order is {order.status}. Ask Admin to reopen it (set back to Pending) to edit.</p>
                  )}
                  <button data-testid="staff-ok-button" onClick={onClose} className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                    <Check className="h-4 w-4" /> OK
                  </button>
                </div>
              )}

              {cancelMode && (
                <div data-testid="staff-cancel-form" className="mt-4 rounded-xl border border-red-200 bg-red-50/50 p-3">
                  <p className="text-sm font-semibold text-slate-800">Cancel this order?</p>
                  <p className="text-xs text-slate-500">The order is retained for records, not deleted.</p>
                  <input data-testid="staff-cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Cancellation reason (required)" className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => { setCancelMode(false); setCancelReason(""); setError(""); }} className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Keep Order</button>
                    <button data-testid="staff-confirm-cancel" onClick={doCancelOrder} disabled={saving} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Cancel"}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div data-testid="staff-order-editor">
              {pickerStep ? (
                <div data-testid="staff-add-product-picker">
                  {pickerStep === "brands" ? (
                    <>
                      <div className="mb-3 flex items-center gap-2">
                        <button data-testid="picker-back-editor" onClick={() => setPickerStep(null)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600"><ArrowLeft className="h-4 w-4" /></button>
                        <h4 className="text-sm font-bold text-slate-800">Select a Brand</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {pickerBrands.map((b) => (
                          <button key={b} data-testid={`picker-brand-${b}`} onClick={() => { setPickerBrand(b); setPickerStep("products"); }} className="rounded-xl border border-slate-100 bg-white px-3 py-3 text-left text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50">
                            {b}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3 flex items-center gap-2">
                        <button data-testid="picker-back-brands" onClick={() => setPickerStep("brands")} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600"><ArrowLeft className="h-4 w-4" /></button>
                        <h4 className="min-w-0 truncate text-sm font-bold text-slate-800">{pickerBrand}</h4>
                      </div>
                      <div className="space-y-1.5">
                        {brandProducts.map((p) => {
                          const o = priceOptions(p)[0];
                          return (
                            <button key={p.id} data-testid={`picker-product-${p.id}`} onClick={() => addProduct(p)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2.5 text-left transition hover:border-blue-300 hover:bg-blue-50">
                              <span className="flex min-w-0 items-center gap-2">
                                <ProductImage path={p.image_path} alt={p.name} iconClass="h-4 w-4" className="h-10 w-10 rounded-lg border border-slate-100" />
                                <span className="min-w-0"><span className="block truncate text-sm font-medium text-slate-700">{p.name}</span>{o && <span className="text-[11px] text-slate-400">{o.label} — {formatMoney(o.rate)}</span>}</span>
                              </span>
                              <Plus className="h-4 w-4 shrink-0 text-blue-600" />
                            </button>
                          );
                        })}
                        {brandProducts.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No sellable products in this brand.</p>}
                      </div>
                    </>
                  )}
                </div>
              ) : (
              <>
              <div className="space-y-2">
                {editItems.map((row, idx) => {
                  const p = productsById[row.product_id];
                  const opts = p ? priceOptions(p) : [];
                  const line = lineFor(row);
                  return (
                    <div key={`${row.product_id}-${idx}`} data-testid={`edit-row-${idx}`} className="rounded-xl border border-slate-100 p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <ProductImage path={p && p.image_path} alt={row.name} className="h-11 w-11 rounded-lg border border-slate-100" />
                          <p className="min-w-0 text-sm font-semibold text-slate-800">{row.name}<span className="ml-1 text-[11px] font-normal text-slate-400">{row.brand}</span></p>
                        </div>
                        <button data-testid={`edit-remove-${idx}`} onClick={() => removeRow(idx)} className="shrink-0 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {opts.length > 0 ? (
                          <select data-testid={`edit-unit-${idx}`} value={row.unit || ""} onChange={(e) => setUnit(idx, e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 outline-none focus:border-blue-500">
                            {opts.map((o) => <option key={o.unit} value={o.unit}>{o.unit} — {formatMoney(o.rate)}</option>)}
                          </select>
                        ) : <span className="text-xs text-slate-400">—</span>}
                        <div className="flex items-center rounded-lg border border-slate-200">
                          <button data-testid={`edit-dec-${idx}`} onClick={() => decQty(idx)} className="grid h-9 w-9 place-items-center text-slate-500 hover:text-blue-600" aria-label="Decrease quantity"><Minus className="h-4 w-4" /></button>
                          <input
                            data-testid={`edit-qty-${idx}`}
                            type="text" inputMode="numeric" pattern="[0-9]*" aria-label="Quantity"
                            value={row.qty === 0 ? "" : row.qty}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.currentTarget.select()}
                            onChange={(e) => { const d = e.target.value.replace(/[^0-9]/g, ""); setQty(idx, d === "" ? 0 : parseInt(d, 10)); }}
                            onBlur={(e) => { const d = e.target.value.replace(/[^0-9]/g, ""); setQty(idx, d === "" || parseInt(d, 10) < 1 ? 1 : parseInt(d, 10)); }}
                            className="h-9 w-16 border-x border-slate-200 bg-white text-center text-sm font-semibold text-slate-700 outline-none focus:bg-blue-50"
                          />
                          <button data-testid={`edit-inc-${idx}`} onClick={() => incQty(idx)} className="grid h-9 w-9 place-items-center text-slate-500 hover:text-blue-600" aria-label="Increase quantity"><Plus className="h-4 w-4" /></button>
                        </div>
                        <span className="ml-auto text-sm font-bold text-blue-700" data-testid={`edit-line-total-${idx}`}>{line ? formatMoney(line.line_total) : "…"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {freeLines.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="edit-free-lines">
                  {freeLines.map((f, i) => (
                    <p key={i} className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">🎁 {f.qty} {f.unit ? `${f.unit} ` : ""}{cleanFreeName(f.name)} — FREE</p>
                  ))}
                </div>
              )}

              <button data-testid="edit-add-product-button" onClick={() => { setPickerStep("brands"); setPickerBrand(null); }} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-blue-300 bg-blue-50/40 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-50">
                <Plus className="h-4 w-4" /> Add another product
              </button>

              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-sm font-bold text-slate-800">
                <span className="flex items-center gap-1.5">Grand Total {previewing && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />}</span>
                <span data-testid="edit-grand-total" className="text-blue-700">{formatMoney(grandTotal) || "₹0"}</span>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button data-testid="staff-cancel-editing" onClick={() => { setEditing(false); setError(""); }} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel Editing</button>
                <button data-testid="staff-save-changes" onClick={() => setConfirmSave(true)} disabled={saving || editItems.length === 0} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                  <Save className="h-4 w-4" /> Save Changes
                </button>
              </div>
              </>
              )}
            </div>
          )}
        </div>

        {confirmSave && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setConfirmSave(false)} />
            <div data-testid="staff-save-confirm" className="relative w-full max-w-xs rounded-2xl bg-white p-5 shadow-2xl">
              <p className="text-base font-bold text-slate-800">Save changes to this order?</p>
              <p className="mt-1 text-sm text-slate-500">Order #{order.id.slice(-6).toUpperCase()} will be updated (same order, same ID).</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setConfirmSave(false)} className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">No</button>
                <button data-testid="staff-save-confirm-yes" onClick={doSave} disabled={saving} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StaffMyOrders({ refreshKey = 0 }) {
  const [orders, setOrders] = useState(null);
  const [preset, setPreset] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [viewOrder, setViewOrder] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    setOrders(null);
    api.get("/staff/orders/my")
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]));
  }, [refreshKey, reloadNonce]);

  const [start, end] = rangeFor(preset, from, to);

  const filtered = useMemo(() => {
    if (!orders) return [];
    return orders.filter((o) => {
      const t = new Date(o.created_at).getTime();
      if (start && t < start.getTime()) return false;
      if (end && t > end.getTime()) return false;
      return true;
    });
  }, [orders, start, end]);

  const totalValue = filtered.reduce((a, o) => a + (o.total || 0), 0);

  const rangeLabel = preset === "all" ? "All Time"
    : (start && end) ? (fmt(start) === fmt(end) ? fmt(start) : `${fmt(start)} — ${fmt(end)}`) : "Select a date range";

  return (
    <div data-testid="staff-my-orders">
      <h2 className="mb-3 text-lg font-bold text-slate-800">My Orders</h2>

      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button key={p.key} data-testid={`staff-orders-date-${p.key}`} onClick={() => setPreset(p.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${preset === p.key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {preset === "custom" && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">From Date</label>
            <input data-testid="staff-orders-from-date" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">To Date</label>
            <input data-testid="staff-orders-to-date" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span data-testid="staff-orders-range-label" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700"><CalendarRange className="h-4 w-4" /> {rangeLabel}</span>
        <span data-testid="staff-orders-total-count" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700"><ShoppingBag className="h-4 w-4 text-blue-400" /> {filtered.length} orders</span>
        <span data-testid="staff-orders-total-value" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-700"><IndianRupee className="h-4 w-4" /> {formatMoney(totalValue) || "₹0"}</span>
      </div>

      <div className="mt-4">
        {orders === null ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-slate-400"><PackageSearch className="h-10 w-10" /><p className="text-sm">No orders in this period.</p></div>
        ) : (
          <div className="space-y-3" data-testid="staff-orders-list">
            {filtered.map((o) => (
              <div key={o.id} data-testid={`staff-order-${o.id}`} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {o.shop_name ? <>{o.shop_name} <span className="font-normal text-slate-400">· {o.customer_name || "—"}</span></> : (o.customer_name || "—")}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">📱 {o.customer_phone || "—"}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
                      <span>#{o.id.slice(-6).toUpperCase()}</span>
                      <span>{new Date(o.created_at).toLocaleString("en-IN")}</span>
                      <span>{(o.items || []).filter((it) => !it.is_free).length} item(s)</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <OrderStatusBadge status={o.status} />
                    <span className="text-sm font-bold text-blue-700">{formatMoney(o.total) || "—"}</span>
                    <button data-testid={`staff-view-order-${o.id}`} onClick={() => setViewOrder(o)} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                      <Eye className="h-3.5 w-3.5" /> View Order
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewOrder && (
        <OrderDetailsModal
          order={viewOrder}
          onClose={() => setViewOrder(null)}
          onUpdated={() => setReloadNonce((n) => n + 1)}
        />
      )}
    </div>
  );
}
